/**
 * Language Learning (completion plan P5).
 *
 * A setup form — the language you are learning, your current level
 * (self-reported), where you will use it, formal or informal, the situation,
 * and an optional goal in your own words — then the SAME coaching session
 * every other practice uses (CoachingSessionView: Written Chat, Live
 * Conversation or Video; Practice or Test). No music, media or add-on is
 * needed for any of it.
 *
 * Nothing here names a persona or rubric. The server validates the profile,
 * resolves the authored scenario, freezes the profile into the attempt, and
 * says CONTENT_INCOMPLETE when a situation has no authored content rather than
 * improvising one. Testing is offered only in the modalities the scenario's
 * authored rubric applies to.
 *
 * Progress (/language-learning/progress) is per language, from canonical
 * results: an unmeasured value is shown as not measured, never as 0, and
 * pronunciation/fluency are UNAVAILABLE until LevelUp Media provides them.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  useGetLanguageLearningCatalogQuery,
  useGetLanguageProgressQuery,
  type LanguageRegister,
  type LanguageScenarioEntry,
  type LanguageSituation,
  type CefrLevel,
} from '../store/api/languageLearningApi';
import { CoachingSessionView, type DisplayPersona, type DisplayScenario } from './CoachingPage';
import { LanguageProgressCard } from '../components/languageLearning/LanguageProgressCard';
import { ConversationEngineOffNotice, useConversationEngineSwitch } from '../components/ConversationEngineOff';
import {
  EMPTY_LANGUAGE_FIELDS,
  buildLanguageLearningRequest,
  languageLearningBlocker,
  scenariosFor,
  type LanguageLearningFields,
} from '../utils/languageLearning';

const GAP_COPY = {
  no_authored_scenario: 'Not available yet: no conversation has been authored for this situation.',
  partner_persona_unavailable: 'Not available yet: the conversation partner has not been authored.',
} as const;

const MODALITY_COPY: Record<string, string> = {
  text: 'Written Chat',
  realtime_audio: 'Live Conversation',
  realtime_audio_video: 'Video',
};

function displayDifficulty(d: string): DisplayScenario['difficulty'] {
  return d === 'beginner' || d === 'intermediate' ? d : 'advanced';
}

interface Started {
  persona: DisplayPersona;
  scenario: DisplayScenario;
  languageLearning: {
    request: ReturnType<typeof buildLanguageLearningRequest>; scenarioId: string; testingModalities: string[];
    developmentModalities?: string[];
  };
}

export function LanguageLearningPage() {
  const engine = useConversationEngineSwitch();
  const { data: catalog, isLoading, isError } = useGetLanguageLearningCatalogQuery(undefined, {
    skip: engine.loading || engine.off,
  });
  const [fields, setFields] = useState<LanguageLearningFields>(EMPTY_LANGUAGE_FIELDS);
  const [started, setStarted] = useState<Started | null>(null);
  const set = (patch: Partial<LanguageLearningFields>) => setFields((f) => ({ ...f, ...patch }));

  const language = useMemo(() => catalog?.languages.find((l) => l.code === fields.language), [catalog, fields.language]);
  const scenarios = scenariosFor(catalog, fields.situation);
  const chosen: LanguageScenarioEntry | undefined = scenarios.find((s) => s.id === fields.scenarioId);
  const blocker = languageLearningBlocker(fields, catalog);
  const objectiveMax = catalog?.limits.objectiveMaxChars ?? 300;

  const handleStart = () => {
    if (blocker || !chosen || !fields.language || !fields.proficiency || !fields.register || !fields.situation) return;
    setStarted({
      persona: {
        // The persona KEY: the server checks it against the scenario's own.
        id: chosen.partner.personaKey,
        name: chosen.partner.label,
        role: 'Conversation partner',
        description: chosen.description,
        personality: [],
        difficulty: 'medium',
      },
      scenario: {
        id: chosen.id,
        title: chosen.title,
        description: chosen.description,
        category: 'Language practice',
        difficulty: displayDifficulty(chosen.difficulty),
        duration: `${chosen.estimatedMinutes} min`,
      },
      languageLearning: {
        request: buildLanguageLearningRequest({
          language: fields.language,
          proficiency: fields.proficiency,
          region: fields.region,
          register: fields.register,
          situation: fields.situation,
          objective: fields.objective,
        }),
        scenarioId: chosen.id,
        testingModalities: chosen.testingModalities,
        ...(chosen.developmentModalities ? { developmentModalities: chosen.developmentModalities } : {}),
      },
    });
  };

  if (engine.loading) return null;
  if (engine.off) {
    return (
      <div className="space-y-8 max-w-3xl">
        <h1 className="text-2xl font-bold text-gray-900">Language Learning</h1>
        <ConversationEngineOffNotice feature="Language Learning" />
      </div>
    );
  }

  if (started) {
    return (
      <CoachingSessionView
        persona={started.persona}
        scenario={started.scenario}
        languageLearning={started.languageLearning}
        onEnd={() => setStarted(null)}
      />
    );
  }

  return (
    <div className="space-y-8 max-w-3xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Language Learning</h1>
          <p className="mt-1 text-gray-500">
            Practise everyday conversations in the language you are learning, pitched to your level, then get feedback on how you used it.
          </p>
        </div>
        <Link to="/language-learning/progress" className="shrink-0 text-sm font-medium text-primary-600 hover:text-primary-700">
          Your progress →
        </Link>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading language practice…</p>}
      {isError && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
          <p className="text-sm text-amber-900">Language Learning is not available right now.</p>
        </div>
      )}

      {catalog && (
        <>
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">1. Your language</h2>
            <label htmlFor="ll-language" className="block text-sm font-medium text-gray-700">Language you are learning</label>
            <select
              id="ll-language"
              value={fields.language ?? ''}
              onChange={(e) => {
                const code = e.target.value || null;
                const regions = catalog.languages.find((l) => l.code === code)?.regions ?? [];
                // A language that is itself a region (es-MX) preselects its usage notes.
                set({ language: code, region: regions.find((r) => r.key === code)?.key ?? null });
              }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Choose a language</option>
              {catalog.languages.filter((l) => l.code !== catalog.baseLanguage).map((l) => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>

            {language && language.regions.length > 0 && (
              <div data-testid="regional-usage">
                <label htmlFor="ll-region" className="block text-sm font-medium text-gray-700">Where you will use it</label>
                <select
                  id="ll-region"
                  value={fields.region ?? ''}
                  onChange={(e) => set({ region: e.target.value || null })}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">No particular region</option>
                  {language.regions.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
                {fields.region && (
                  <ul className="mt-2 list-disc pl-5 text-xs text-gray-600">
                    {language.regions.find((r) => r.key === fields.region)?.usage.map((u) => <li key={u}>{u}</li>)}
                  </ul>
                )}
                <p className="mt-1 text-xs text-gray-500">Regional choices change vocabulary and forms of address — nothing else.</p>
              </div>
            )}
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">2. Your current level</h2>
            <p className="text-xs text-gray-500" data-testid="self-reported-note">
              Self-reported: choose the description that fits you best. It sets how your partner speaks; it is not a test result.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label="Current level (self-reported)">
              {catalog.proficiency.levels.map((l) => (
                <button
                  key={l.level}
                  type="button"
                  role="radio"
                  aria-checked={fields.proficiency === l.level}
                  onClick={() => set({ proficiency: l.level as CefrLevel })}
                  className={clsx(
                    'text-left rounded-lg border p-3 text-sm',
                    fields.proficiency === l.level ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                  )}
                >
                  <span className="font-semibold">{l.level}</span> <span className="text-gray-600">{l.description}</span>
                </button>
              ))}
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">3. The conversation</h2>
            <div className="flex gap-2" role="radiogroup" aria-label="Register">
              {catalog.registers.map((r) => (
                <button
                  key={r}
                  type="button"
                  role="radio"
                  aria-checked={fields.register === r}
                  onClick={() => set({ register: r as LanguageRegister })}
                  className={clsx('rounded-full px-3 py-1.5 text-sm', fields.register === r ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700')}
                >
                  {r === 'formal' ? 'Formal' : 'Informal'}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" role="radiogroup" aria-label="Situation">
              {catalog.situations.map((s) => {
                const disabled = s.status !== 'available';
                return (
                  <button
                    key={s.situation}
                    type="button"
                    role="radio"
                    aria-checked={fields.situation === s.situation}
                    disabled={disabled}
                    data-status={disabled ? 'CONTENT_INCOMPLETE' : 'available'}
                    onClick={() => set({ situation: s.situation as LanguageSituation, scenarioId: s.scenarios[0]?.id ?? null })}
                    className={clsx(
                      'text-left rounded-lg border p-4 disabled:cursor-not-allowed disabled:opacity-60',
                      fields.situation === s.situation ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <div className="font-medium text-gray-900">{s.label}</div>
                    <p className="mt-1 text-xs text-gray-600">{s.description}</p>
                    {disabled && s.gap && <p className="mt-2 text-xs text-gray-500">{GAP_COPY[s.gap]}</p>}
                  </button>
                );
              })}
            </div>
            {scenarios.length > 0 && (
              <div className="space-y-2" role="radiogroup" aria-label="Conversation">
                {scenarios.map((sc) => (
                  <button
                    key={sc.id}
                    type="button"
                    role="radio"
                    aria-checked={fields.scenarioId === sc.id}
                    onClick={() => set({ scenarioId: sc.id })}
                    className={clsx(
                      'w-full text-left rounded-lg border p-3',
                      fields.scenarioId === sc.id ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <div className="font-medium text-gray-900">{sc.title}</div>
                    <p className="text-xs text-gray-600">{sc.description}</p>
                    <p className="mt-1 text-xs text-gray-500" data-testid={`testing-modalities-${sc.id}`}>
                      {sc.testingModalities.length
                        ? `Can be taken as a test in: ${sc.testingModalities.map((m) => MODALITY_COPY[m] ?? m).join(', ')}`
                        : 'Practice only: no test is authored for this conversation.'}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-semibold text-gray-900">4. Your goal (optional)</h2>
            <textarea
              id="ll-objective"
              aria-label="Your goal"
              value={fields.objective}
              maxLength={objectiveMax}
              onChange={(e) => set({ objective: e.target.value })}
              rows={3}
              placeholder="e.g. Order for my family without switching to English."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>Used only for this practice session.</span>
              <span className="tabular-nums">{fields.objective.length}/{objectiveMax}</span>
            </div>
          </section>

          <div className="flex items-center justify-between gap-4">
            <p className="text-xs text-gray-500">You can choose Written Chat, Live Conversation or Video, and Practice or Test, on the next screen.</p>
            <button
              type="button"
              onClick={handleStart}
              disabled={!!blocker}
              title={blocker ?? undefined}
              className="btn-primary px-6 py-3 text-base disabled:opacity-50"
            >
              Start practising
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export function LanguageProgressPage() {
  const engine = useConversationEngineSwitch();
  const { data, isLoading, isError } = useGetLanguageProgressQuery(undefined, { skip: engine.loading || engine.off });
  if (engine.loading) return null;
  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <Link to="/language-learning" className="text-sm text-primary-600 hover:text-primary-700">← Language Learning</Link>
        <h1 className="mt-1 text-2xl font-bold text-gray-900">Your language progress</h1>
        <p className="mt-1 text-sm text-gray-500">
          From your completed conversations. Skills that were not measured in a conversation are counted as not measured, not as zero.
        </p>
      </div>
      {engine.off && <ConversationEngineOffNotice feature="Language Learning" />}
      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {isError && <p className="text-sm text-amber-800">Progress is not available right now.</p>}
      {data && data.length === 0 && <p className="text-sm text-gray-600">No completed language practice yet.</p>}
      {data?.map((p) => <LanguageProgressCard key={p.language} progress={p} />)}
    </div>
  );
}

export default LanguageLearningPage;
